import type {
  ChatModelAdapter,
  ChatModelRunOptions,
} from "@assistant-ui/react";
import {
  CuadraApiError,
  CuadraChatClient,
  parseSSEStream,
  type SSEChunk,
} from "../lib/cuadraChatClient";
import { attachmentErrorStore } from "./attachmentErrorStore";
import { artifactStore } from "./artifactStore";
import { chatErrorStore } from "./chatErrorStore";
import {
  convertToCuadraMessages,
  getLatestUserText,
} from "./messageConverter";
import {
  appendToConversationHistory,
  clearConversationHistory,
  getPreMadeConversationHistory,
  queuePreMadeResponse,
  shiftPendingPreMade,
} from "./preMadeResponseHandler";
import {
  activeStreamingChatIds,
  activeThreadRemoteId,
  localToRemoteIdMap,
  messageIdToChatIdMap,
} from "./threadListAdapter";
import { streamingMetadataStore } from "./streamingMetadataStore";
import { reasoningPhaseStore } from "./reasoningPhaseStore";
import { backgroundGenerationStore } from "./backgroundGenerationStore";
import { isZipArchiveFile } from "../utils/isZipArchiveFile";
import type { ChatRequest, Source, ToolDefinition } from "../types/cuadra";

/**
 * Treat 402 as credits; 403 only when the API message suggests quota/credits.
 * 403 is also used for RBAC (e.g. "Insufficient permissions") and must not
 * show the credits upgrade toast.
 */
function isCuadraInsufficientCreditsError(err: CuadraApiError): boolean {
  if (err.status === 402) return true;
  if (err.status !== 403) return false;
  const m = err.message.toLowerCase();
  return (
    m.includes("credit") ||
    m.includes("quota") ||
    m.includes("balance") ||
    m.includes("overage") ||
    (m.includes("usage") && m.includes("limit"))
  );
}
import {
  type ArtifactDeltaEvent,
  type ArtifactEndEvent,
  type ArtifactErrorEvent,
  type ArtifactStartEvent,
  isDocumentArtifactGenerating,
} from "../types/artifact";
// Re-export for convenience
export type { StreamingMetadata } from "./streamingMetadataStore";
export { streamingMetadataStore } from "./streamingMetadataStore";
export { reasoningPhaseStore } from "./reasoningPhaseStore";
export type { ReasoningPhase, ReasoningPhaseState } from "./reasoningPhaseStore";
export { artifactStore } from "./artifactStore";
export {
  queuePreMadeResponse,
  hasPendingPreMadeResponse,
  clearPreMadeResponses,
  getPreMadeConversationHistory,
} from "./preMadeResponseHandler";
export { backgroundGenerationStore } from "./backgroundGenerationStore";

/**
 * Non-streaming POST /v1/chats (tool ack) returns the model's next assistant turn as JSON.
 * Supports top-level `message` or wrapped `data.message`.
 */
function extractAssistantFollowupFromChatJson(
  data: unknown,
): { content: string; messageId?: string } | null {
  if (!data || typeof data !== "object") return null;
  const root = data as Record<string, unknown>;
  const nested =
    root.data !== undefined &&
    root.data !== null &&
    typeof root.data === "object"
      ? (root.data as Record<string, unknown>)
      : null;
  const bag = nested ?? root;
  const msg = bag.message;
  if (!msg || typeof msg !== "object") return null;
  const m = msg as Record<string, unknown>;
  if (m.role !== "assistant") return null;
  const raw =
    typeof m.content === "string"
      ? m.content
      : m.content === null || m.content === undefined
        ? ""
        : "";
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const messageId =
    typeof m.id === "string" && m.id.length > 0 ? m.id : undefined;
  return { content: trimmed, messageId };
}

export interface ChatModelAdapterOptions {
  modelId?: string | null;
  getModelId?: () => string | null | undefined;
  systemPrompt?: string;
  ephemeral?: boolean;
  enableReasoning?: boolean;
  enableArtifacts?: boolean;
  /**
   * Optional tool definitions passed through to the API for this chat request.
   * Keep this small; it is included in every completion call.
   */
  tools?: ToolDefinition[];
  /**
   * Optional callback for non-artifact tool calls emitted by the model.
   * Intended for the host app (e.g. dashboard) to open a modal / route users.
   * `ctx.lastUserText` is the current user turn (plain text) for routing fixes.
   * `ctx.modelId` is the resolved model for this request (fixed or selector), when known.
   */
  onToolCall?: (
    toolName: string,
    args: Record<string, unknown>,
    ctx?: { lastUserText: string; modelId?: string },
  ) => void;
  onChatCreated?: (chatId: string) => void;
  onUserMessage?: () => void;
  onThreadIdUpdate?: (localThreadId: string, serverChatId: string) => void;
  /** Called when the stream ends (success or error). Use to refresh artifact list. */
  onStreamEnd?: (chatId: string) => void;
  onBeforeRequest?: () => boolean | Promise<boolean>;
  mockResponseOnBlocked?: string | (() => string);
  /** Called when artifact generation begins (create_artifact tool call detected). */
  onArtifactGenerating?: (chatId: string) => void;
  /** Called when artifact generation completes (data-artifact-end received). */
  onArtifactComplete?: (chatId: string, artifactId?: string) => void;
  /**
   * Stable id for this widget/runtime so attachment and stream errors do not
   * leak across multiple mounted `CuadraChat` instances (set by CuadraRuntimeProvider).
   */
  surfaceId?: string;
}

/**
 * Creates a ChatModelAdapter for LocalRuntime that connects to Cuadra API
 * Handles streaming responses and converts between assistant-ui and API formats
 */
export function createChatModelAdapter(
  client: CuadraChatClient,
  options: ChatModelAdapterOptions,
): ChatModelAdapter {
  const {
    modelId: staticModelId,
    getModelId,
    systemPrompt,
    ephemeral,
    enableReasoning,
    enableArtifacts,
    tools,
    onToolCall,
    onChatCreated,
    onUserMessage,
    onThreadIdUpdate,
    onStreamEnd,
    onBeforeRequest,
    mockResponseOnBlocked,
    onArtifactGenerating,
    onArtifactComplete,
    surfaceId,
  } = options;
  const resolveModelId = () =>
    (getModelId ? getModelId() : staticModelId) ?? null;

  const pushAttachmentError = (message: string, code: string) => {
    attachmentErrorStore.push(message, code, surfaceId);
  };

  return {
    async *run(options: ChatModelRunOptions) {
      const { messages, abortSignal } = options;
      const buildToolCallCtx = (): { lastUserText: string; modelId?: string } => {
        const lastUserText = getLatestUserText(messages);
        const mid = resolveModelId();
        return {
          lastUserText,
          ...(mid && mid.trim() !== "" ? { modelId: mid } : {}),
        };
      };
      // unstable_threadId is the remoteId of the current thread, provided
      // directly by the runtime (available since @assistant-ui/react 0.12).
      // For brand-new threads it may be undefined at run start.
      const threadId = (options as { unstable_threadId?: string })
        .unstable_threadId;

      // REQ-10: Cancel any active background-generation polling when a new
      // stream starts (user sent a new message or retried).
      backgroundGenerationStore.onNewStream();

      const pendingPreMade = shiftPendingPreMade();
      if (pendingPreMade) {
        // Simulate the API response with streaming
        onUserMessage?.();

        // Initial "thinking" delay before streaming starts
        const initialDelay = pendingPreMade.initialDelay ?? 800;
        if (initialDelay > 0) {
          await new Promise((resolve) => setTimeout(resolve, initialDelay));
        }

        // Check if aborted during delay
        if (abortSignal?.aborted) {
          throw new Error("Request aborted");
        }

        const words = pendingPreMade.response.split(" ");
        let accumulatedText = "";
        const speed = pendingPreMade.streamingSpeed || 50;

        // Generate a fake chat ID for ephemeral pre-made chats
        const fakeChatId = `premade-${Date.now()}`;

        // If ephemeral, we don't need a real chat ID
        // If not ephemeral, you might want to still create a real chat
        if (ephemeral) {
          onChatCreated?.(fakeChatId);
        }

        // Simulate streaming word by word
        for (let i = 0; i < words.length; i++) {
          if (abortSignal?.aborted) {
            throw new Error("Request aborted");
          }

          accumulatedText += (i === 0 ? "" : " ") + words[i];

          yield {
            content: [{ type: "text", text: accumulatedText }],
          };

          // Delay between words for streaming effect
          await new Promise((resolve) =>
            setTimeout(resolve, speed + Math.random() * (speed * 0.5)),
          );
        }

        // Final yield
        yield {
          content: [{ type: "text", text: accumulatedText }],
        };

        appendToConversationHistory(
          pendingPreMade.question,
          pendingPreMade.response,
        );

        return;
      }

      // Resolve the server chat ID for this conversation.
      // The history loader registers every message ID → chatId in
      // messageIdToChatIdMap. We scan the current messages for a match.
      // For new threads (first message), no match is found and the server
      // creates a new chat.
      let serverChatId: string | undefined;

      for (const msg of messages) {
        const mapped = messageIdToChatIdMap.get(msg.id);
        if (mapped) {
          serverChatId = mapped;
          break;
        }
      }

      /** Used when SSE first returns a chat id — avoids skipping `onChatCreated` when thread ids are not wired yet. */
      const hadPersistedServerChatBeforeStream = Boolean(serverChatId);

      onUserMessage?.();

      // Convert assistant-ui ThreadMessage format to API MessageCreate format.
      //
      // When continuing an existing chat (serverChatId is set) the backend
      // already has the full conversation history persisted in the DB and
      // prepends it automatically.  We must only send the *new* user
      // message to avoid duplicating rows on retries after a failed stream.
      let messagesToSend = messages;

      if (serverChatId) {
        // Find the last user message — that is the only new content.
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].role === "user") {
            messagesToSend = [messages[i]];
            break;
          }
        }
      } else {
        // New chat (no serverChatId yet): check if we already have
        // assistant responses (from pre-made Q&A or similar flows) and
        // trim to only the messages after the last assistant response.
        let lastAssistantIndex = -1;
        for (let i = messages.length - 1; i >= 0; i--) {
          if (messages[i].role === "assistant") {
            lastAssistantIndex = i;
            break;
          }
        }
        if (lastAssistantIndex >= 0) {
          messagesToSend = messages.slice(lastAssistantIndex + 1);
        }
      }

      const apiMessages = convertToCuadraMessages(messagesToSend);

      const preMadeHistory = getPreMadeConversationHistory();
      if (preMadeHistory.length > 0 && !serverChatId) {
        const preMadeApiMessages = preMadeHistory.map((msg) => ({
          role: msg.role as "user" | "assistant",
          content: msg.content,
        }));
        apiMessages.unshift(...preMadeApiMessages);
        clearConversationHistory();
      }

      // Upload any file attachments from the last user message
      const fileIds: string[] = [];
      let lastUserMessage: (typeof messagesToSend)[number] | undefined;
      for (let i = messagesToSend.length - 1; i >= 0; i--) {
        if (messagesToSend[i]!.role === "user") {
          lastUserMessage = messagesToSend[i];
          break;
        }
      }
      if (lastUserMessage && "attachments" in lastUserMessage) {
        const attachments = (
          lastUserMessage as { attachments?: Array<{ file?: File }> }
        ).attachments;
        if (attachments && attachments.length > 0) {
          for (const attachment of attachments) {
            if (attachment.file) {
              try {
                if (isZipArchiveFile(attachment.file)) {
                  const result = await client.expandZipFile(
                    attachment.file,
                    serverChatId ? "chat" : undefined,
                    serverChatId,
                  );
                  for (const f of result.files) {
                    fileIds.push(f.id);
                  }
                  if (
                    result.skipped.length > 0 &&
                    result.files.length === 0
                  ) {
                    const reasons = result.skipped
                      .map((s) => `${s.filename}: ${s.reason}`)
                      .join("; ");
                    pushAttachmentError(
                      `All files in the ZIP were skipped: ${reasons}`,
                      "ZIP_ALL_SKIPPED",
                    );
                    yield { content: [{ type: "text" as const, text: "" }] };
                    return;
                  }
                } else {
                  const uploaded = await client.uploadFile(
                    attachment.file,
                    serverChatId ? "chat" : undefined,
                    serverChatId,
                  );
                  fileIds.push(uploaded.id);
                }
              } catch (uploadError) {
                if (uploadError instanceof CuadraApiError) {
                  const isCredits =
                    isCuadraInsufficientCreditsError(uploadError);
                  const code = isCredits ? "INSUFFICIENT_CREDITS" : "API_ERROR";
                  pushAttachmentError(uploadError.message, code);
                  yield { content: [{ type: "text" as const, text: "" }] };
                  return;
                }
              }
            }
          }
        }
      }

      const request: {
        messages: typeof apiMessages;
        chatId: string | null;
        modelId?: string | null;
        systemPrompt?: string;
        ephemeral?: boolean;
        enableReasoning?: boolean;
        enableArtifacts?: boolean;
        tools?: ToolDefinition[];
        stream: boolean;
        fileIds?: string[];
      } = {
        messages: apiMessages,
        chatId: serverChatId || null,
        systemPrompt,
        ephemeral,
        enableReasoning,
        enableArtifacts,
        stream: true,
      };

      // Only include modelId if provided (backend may resolve it)
      const currentModelId = resolveModelId();
      if (currentModelId && currentModelId.trim() !== "") {
        request.modelId = currentModelId;
      }

      // Include file IDs if any files were uploaded
      if (fileIds.length > 0) {
        request.fileIds = fileIds;
      }

      // Include tools if provided (normalize to OpenAI tool schema expected by API)
      if (tools && tools.length > 0) {
        request.tools = tools
          .map((t) => {
            if (t && t.type === "function" && t.function?.name) {
              return t;
            }
            if (t?.name) {
              return {
                type: "function" as const,
                function: {
                  name: t.name,
                  description: t.description,
                  parameters: t.parameters,
                },
              } satisfies ToolDefinition;
            }
            return null;
          })
          .filter(Boolean) as ToolDefinition[];
      }

      // Call interceptor before making the API request
      if (onBeforeRequest) {
        const shouldProceed = await onBeforeRequest();
        if (!shouldProceed) {
          // Request was blocked by interceptor
          // If mockResponseOnBlocked is set, show it instead of throwing
          if (mockResponseOnBlocked) {
            // Get the response text (call function if it's a function)
            const responseText =
              typeof mockResponseOnBlocked === "function"
                ? mockResponseOnBlocked()
                : mockResponseOnBlocked;

            // Get the last user message to use as the "question"
            const userMessages = messages.filter((m) => m.role === "user");
            const lastUserMessage =
              userMessages.length > 0
                ? userMessages[userMessages.length - 1]
                : undefined;
            let userQuestion = "Message";
            if (lastUserMessage) {
              if (typeof lastUserMessage.content === "string") {
                userQuestion = lastUserMessage.content;
              } else if (Array.isArray(lastUserMessage.content)) {
                const textPart = lastUserMessage.content.find(
                  (c: { type: string; text?: string }) => c.type === "text",
                ) as { type: string; text?: string } | undefined;
                userQuestion = textPart?.text || "Message";
              }
            }

            queuePreMadeResponse(userQuestion, responseText, 40, 400);
            const pendingMock = shiftPendingPreMade();
            if (pendingMock) {
              onUserMessage?.();

              // Initial "thinking" delay before streaming starts
              const initialDelay = pendingMock.initialDelay ?? 400;
              if (initialDelay > 0) {
                await new Promise((resolve) =>
                  setTimeout(resolve, initialDelay),
                );
              }

              // Check if aborted during delay
              if (abortSignal?.aborted) {
                throw new Error("Request aborted");
              }

              const words = pendingMock.response.split(" ");
              let accumulatedText = "";
              const speed = pendingMock.streamingSpeed || 40;

              // Generate a fake chat ID for the mock response
              const fakeChatId = `mock-${Date.now()}`;
              if (ephemeral) {
                onChatCreated?.(fakeChatId);
              }

              // Simulate streaming word by word
              for (let i = 0; i < words.length; i++) {
                if (abortSignal?.aborted) {
                  throw new Error("Request aborted");
                }

                accumulatedText += (i === 0 ? "" : " ") + words[i];

                yield {
                  content: [{ type: "text", text: accumulatedText }],
                };

                // Delay between words for streaming effect
                await new Promise((resolve) =>
                  setTimeout(resolve, speed + Math.random() * (speed * 0.3)),
                );
              }

              // Final yield
              yield {
                content: [{ type: "text", text: accumulatedText }],
              };

              return;
            }
          }

          throw new Error("Request blocked");
        }
      }

      // ---------------------------------------------------------------
      // Background-safe streaming: decouple the HTTP fetch lifecycle
      // from the generator so navigation doesn't kill in-flight streams.
      // ---------------------------------------------------------------

      // Separate controller for the fetch — only abort during the
      // initial POST (before we start reading the response body).
      // Once the stream starts, navigation will stop the generator's
      // yield loop but the background consumer keeps the connection
      // alive so the server can finish processing.
      const fetchController = new AbortController();
      let streamObtained = false;
      const forwardAbort = () => {
        if (!streamObtained) fetchController.abort();
      };
      abortSignal?.addEventListener("abort", forwardAbort, { once: true });

      let stream: ReadableStream<Uint8Array>;
      try {
        stream = await client.createOrContinueChat(
          request,
          fetchController.signal,
        );
      } catch (err) {
        abortSignal?.removeEventListener("abort", forwardAbort);
        if (err instanceof CuadraApiError) {
          const isCredits = isCuadraInsufficientCreditsError(err);
          const code = isCredits ? "INSUFFICIENT_CREDITS" : "API_ERROR";
          pushAttachmentError(err.message, code);
          yield { content: [{ type: "text" as const, text: "" }] };
          return;
        }
        throw err;
      }
      streamObtained = true;
      abortSignal?.removeEventListener("abort", forwardAbort);

      // --- Background SSE consumer ---
      // Reads chunks from the SSE stream independently of the generator.
      // When the generator is interrupted (navigation / unmount) the
      // consumer keeps draining data so the server completes its work
      // and the response is persisted.
      const chunkQueue: SSEChunk[] = [];
      let bgDone = false;
      let bgError: Error | null = null;
      const waiter = { notify: null as (() => void) | null };

      const bgPromise = (async () => {
        try {
          for await (const chunk of parseSSEStream(stream)) {
            chunkQueue.push(chunk);
            waiter.notify?.();
            waiter.notify = null;
            // Do not stop on `finished: true` alone — some APIs emit an early
            // `finished` flag and still send more `toolCalls` argument deltas
            // (or a second `finished` with usage). Drain until [DONE] / `done`
            // or the HTTP stream ends.
            if (chunk.done) break;
          }
        } catch (e) {
          bgError = e instanceof Error ? e : new Error(String(e));
        } finally {
          bgDone = true;
          waiter.notify?.();
          waiter.notify = null;
        }
      })();
      bgPromise.catch(() => {});

      async function nextChunk(): Promise<SSEChunk | null> {
        while (chunkQueue.length === 0 && !bgDone) {
          if (abortSignal?.aborted) return null;
          let onAbort: (() => void) | null = null;
          await new Promise<void>((resolve) => {
            waiter.notify = resolve;
            if (abortSignal && !abortSignal.aborted) {
              onAbort = () => {
                waiter.notify = null;
                resolve();
              };
              abortSignal.addEventListener("abort", onAbort, { once: true });
            }
          });
          if (onAbort && abortSignal) {
            abortSignal.removeEventListener("abort", onAbort);
          }
          if (abortSignal?.aborted) return null;
        }
        if (bgError && chunkQueue.length === 0) throw bgError;
        return chunkQueue.shift() ?? null;
      }

      // --- Chunk processing state ---
      let accumulatedText = "";
      let accumulatedReasoning = "";
      let chatId: string | undefined;
      let serverMessageId: string | undefined;
      const sources: Source[] = [];
      let lastSourcesCount = 0;
      let hasAddedReasoningToTextDelay = false;
      let streamErrorReceived = false;
      let streamFinished = false;
      /** Host onToolCall ended the turn; must not treat as interrupted SSE or empty stream. */
      let completedAfterHostToolCall = false;

      const streamMessageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      streamingMetadataStore.clear();
      streamingMetadataStore.setCurrentMessage(streamMessageId);
      reasoningPhaseStore.reset();

      // Message-stream artifact tool calls: accumulate arguments and inject marker when complete.
      // Use a stable key across chunks (later chunks may omit chunk.id); lock to first id seen.
      const toolCallArgsAccumulator = new Map<string, string>();
      // Some providers only include function.name in the first toolCalls chunk and stream
      // arguments later with name omitted. Keep the last seen name per tool-call slot.
      const toolCallNameAccumulator = new Map<string, string>();
      // OpenAI-style tool call id (first chunk often carries it; later chunks may omit).
      const toolCallIdAccumulator = new Map<string, string>();
      const artifactMarkerEmittedKeys = new Set<string>();
      const hostToolCallEmittedKeys = new Set<string>();
      let toolCallMessageKey = streamMessageId;

      let artifactGeneratingFired = false;
      /** True while streamed toolCalls JSON is still incomplete (Cuadra may emit `finished` early). */
      const hasIncompleteStreamedToolWork = (): boolean => {
        for (const [key, combined] of toolCallArgsAccumulator) {
          if (hostToolCallEmittedKeys.has(key)) continue;
          if (artifactMarkerEmittedKeys.has(key)) continue;
          const name = toolCallNameAccumulator.get(key);
          if (!name) {
            if (combined.length > 0) return true;
            continue;
          }
          if (
            enableArtifacts &&
            (name === "create_artifact" || name === "update_artifact")
          ) {
            try {
              const args = JSON.parse(combined) as Record<string, unknown>;
              const identifier =
                typeof args.identifier === "string" ? args.identifier : "";
              const title = typeof args.title === "string" ? args.title : "";
              const type = typeof args.type === "string" ? args.type : "";
              if (!(identifier && title && type)) return true;
            } catch {
              return true;
            }
            continue;
          }
          if (onToolCall) {
            try {
              JSON.parse(combined);
            } catch {
              return true;
            }
          }
        }
        return false;
      };

      /** Default ack for host-executed tools (dashboard router, etc.). */
      const HOST_TOOL_ACK_JSON = JSON.stringify({ ok: true });

      /** Append assistant text from the tool-ack JSON response into this streamed turn. */
      const mergeFollowupFromToolAckResponse = (data: unknown) => {
        const follow = extractAssistantFollowupFromChatJson(data);
        if (!follow) return;
        const cur = accumulatedText.trimEnd();
        accumulatedText = cur ? `${cur}\n\n${follow.content}` : follow.content;
        if (follow.messageId) {
          serverMessageId = follow.messageId;
        }
      };

      /** POST tool result (non-stream) and merge the API's assistant follow-up into the UI. */
      const postToolAckAndMerge = async (payload: ChatRequest) => {
        const raw = await client.submitToolResult(payload, abortSignal);
        mergeFollowupFromToolAckResponse(raw);
      };

      /** Persist tool result so the server chat can accept the next user message. */
      const submitHostToolAck = async (slotKey: string) => {
        const toolCallId = toolCallIdAccumulator.get(slotKey);
        if (!chatId || !toolCallId) return;
        const payload: ChatRequest = {
          chatId,
          messages: [
            {
              role: "tool",
              content: HOST_TOOL_ACK_JSON,
              toolCallId,
            },
          ],
          stream: false,
        };
        const mid = resolveModelId();
        if (mid && mid.trim() !== "") {
          payload.modelId = mid;
        }
        await postToolAckAndMerge(payload);
      };

      const updateStoreIfNeeded = () => {
        const sourcesChanged = sources.length !== lastSourcesCount;
        if (sourcesChanged) {
          lastSourcesCount = sources.length;
          streamingMetadataStore.update({
            sources: sources.length > 0 ? [...sources] : undefined,
          });
        }
      };

      const createYieldObject = () => {
        updateStoreIfNeeded();
        const content: Array<{ type: "reasoning" | "text"; text: string }> = [];
        // Reasoning always comes before the text response
        if (accumulatedReasoning) {
          content.push({
            type: "reasoning" as const,
            text: accumulatedReasoning,
          });
        }
        if (accumulatedText) {
          content.push({ type: "text" as const, text: accumulatedText });
        }
        if (content.length === 0) {
          content.push({ type: "text" as const, text: "" });
        }
        return {
          content,
          metadata: {
            custom: {
              ...(chatId && { cuadraChatId: chatId }),
              ...(serverMessageId && {
                cuadraServerMessageId: serverMessageId,
              }),
            },
          },
        };
      };

      try {
        let chunk: SSEChunk | null;
        streamLoop: while ((chunk = await nextChunk())) {
          // Handle chat ID from chunk (camelCase + legacy snake_case `chat_id`)
          const chunkId =
            chunk.id ||
            chunk.chatId ||
            chunk.messageId ||
            (typeof chunk.chat_id === "string" && chunk.chat_id.trim() !== ""
              ? chunk.chat_id
              : undefined);
          if (chunk.id) {
            toolCallMessageKey = chunk.id;
          }
          if (chunk.messageId && !serverMessageId) {
            serverMessageId = chunk.messageId;
          }
          if (chunkId && !chatId) {
            chatId = chunkId;

            activeStreamingChatIds.add(chatId);

            const threadIdForStorage = threadId || activeThreadRemoteId;
            const isNewChat =
              threadIdForStorage && !localToRemoteIdMap.has(threadIdForStorage);

            for (const msg of messages) {
              messageIdToChatIdMap.set(msg.id, chunkId);
            }

            if (
              threadIdForStorage &&
              !localToRemoteIdMap.has(threadIdForStorage)
            ) {
              localToRemoteIdMap.set(threadIdForStorage, chunkId);
            }

            // Notify host (replaceState, sidebar, etc.) when we first learn the server chat id.
            // `isNewChat` alone is false for brand-new threads: `unstable_threadId` and
            // `activeThreadRemoteId` can both be unset until after the first chunk.
            // `hadPersistedServerChatBeforeStream` avoids duplicate notifies on later turns.
            const shouldNotifyHostNewChat =
              !hadPersistedServerChatBeforeStream &&
              (isNewChat || !threadIdForStorage);
            if (shouldNotifyHostNewChat) {
              onChatCreated?.(chunkId);
            }

            if (onThreadIdUpdate && threadIdForStorage) {
              onThreadIdUpdate(threadIdForStorage, chunkId);
            }
          }

          // Handle sources array (sent in first chunk typically)
          const chunkSources = (
            chunk as SSEChunk & { sources?: Source[] | null }
          ).sources;
          if (
            chunkSources &&
            Array.isArray(chunkSources) &&
            chunkSources.length > 0
          ) {
            for (const source of chunkSources) {
              if (!sources.some((s) => s.sourceId === source.sourceId)) {
                sources.push({
                  sourceId: source.sourceId,
                  filename: source.filename || "Unknown",
                  score: source.score ?? 1.0,
                  chunkId: source.chunkId,
                  datasetId: source.datasetId,
                  contentType: source.contentType ?? null,
                });
              }
            }
          }

          // Handle reasoning field (string delta on each chunk)
          const chunkReasoning = (chunk as SSEChunk & { reasoning?: string })
            .reasoning;
          if (chunkReasoning && chunkReasoning.length > 0) {
            accumulatedReasoning += chunkReasoning;
            reasoningPhaseStore.setStreaming();
            yield createYieldObject();
          }

          // Handle AI SDK Protocol events (for future compatibility)
          if (chunk.type) {
            switch (chunk.type) {
              case "source-document":
                if (chunk.sourceId) {
                  sources.push({
                    sourceId: chunk.sourceId,
                    filename: chunk.title || "Unknown",
                    score: 1.0,
                  });
                }
                continue;

              case "reasoning-start":
                continue;

              case "reasoning-delta":
                if (chunk.delta) {
                  accumulatedReasoning += chunk.delta;
                }
                if (accumulatedText || accumulatedReasoning) {
                  yield createYieldObject();
                }
                continue;

              case "reasoning-end":
                if (accumulatedText || accumulatedReasoning) {
                  yield createYieldObject();
                }
                continue;

              case "text-delta":
                if (chunk.delta) {
                  if (
                    accumulatedReasoning &&
                    !hasAddedReasoningToTextDelay &&
                    !accumulatedText
                  ) {
                    hasAddedReasoningToTextDelay = true;
                    reasoningPhaseStore.setDone();
                    await new Promise((resolve) => setTimeout(resolve, 400));
                  }
                  accumulatedText += chunk.delta;
                }
                if (accumulatedText) {
                  yield createYieldObject();
                }
                continue;

              case "error": {
                streamErrorReceived = true;
                const errorMsg = chunk.errorText || "Stream error";
                const isCredits =
                  errorMsg.includes("credit") || errorMsg.includes("quota");
                const isOverage = errorMsg.includes("overage");
                const isPremium =
                  errorMsg.includes("premium") || errorMsg.includes("upgrade");
                const isDailyLimit =
                  errorMsg.includes("daily") && errorMsg.includes("limit");
                const code = isDailyLimit
                  ? "DAILY_LIMIT_EXHAUSTED"
                  : isOverage
                    ? "CREDIT_OVERAGE"
                    : isCredits
                      ? "INSUFFICIENT_CREDITS"
                      : isPremium
                        ? "PREMIUM_MODEL_UPGRADE"
                        : "SERVICE_ERROR";
                pushAttachmentError(errorMsg, code);
                continue;
              }

              case "start-step":
                accumulatedReasoning = "";
                continue;

              case "text-start":
              case "text-end":
              case "start":
              case "finish-step":
                continue;

              case "data-artifact-start":
                if (enableArtifacts && chunk.data) {
                  artifactStore.onArtifactStart(
                    chunk.data as ArtifactStartEvent,
                  );
                }
                continue;

              case "data-artifact-delta":
                if (enableArtifacts && chunk.data) {
                  artifactStore.onArtifactDelta(
                    chunk.data as ArtifactDeltaEvent,
                  );
                }
                continue;

              case "data-artifact-end":
                if (enableArtifacts && chunk.data) {
                  const endEvent = chunk.data as ArtifactEndEvent;
                  artifactStore.onArtifactEnd(endEvent);
                  if (chatId) onArtifactComplete?.(chatId, endEvent.id);
                }
                continue;

              case "data-artifact-error":
                if (enableArtifacts && chunk.data) {
                  artifactStore.onArtifactError(
                    chunk.data as ArtifactErrorEvent,
                  );
                }
                continue;

              case "tool-input-start":
                if (
                  enableArtifacts &&
                  chunk.toolName &&
                  (chunk.toolName === "create_artifact" ||
                    chunk.toolName === "update_artifact")
                ) {
                  artifactStore.beginDocumentGeneration();
                  if (!artifactGeneratingFired && chatId) {
                    artifactGeneratingFired = true;
                    onArtifactGenerating?.(chatId);
                  }
                }
                continue;

              case "tool-input-delta":
                continue;

              case "tool-input-available":
                if (
                  enableArtifacts &&
                  chunk.toolName &&
                  (chunk.toolName === "create_artifact" ||
                    chunk.toolName === "update_artifact")
                ) {
                  artifactStore.beginDocumentGeneration();
                  if (!artifactGeneratingFired && chatId) {
                    artifactGeneratingFired = true;
                    onArtifactGenerating?.(chatId);
                  }
                  const args = (chunk.args ?? {}) as Record<string, unknown>;
                  const artifactId =
                    (args.id as string) ?? (args.identifier as string) ?? "";
                  const rawTitle = (args.title as string) ?? "";
                  const rawType = (args.type as string) ?? "";
                  const isUpdateTool = chunk.toolName === "update_artifact";
                  const lookup =
                    artifactId && isUpdateTool
                      ? artifactStore.getArtifact(
                          (args.identifier as string) || artifactId,
                        )
                      : undefined;
                  const artifactTitle =
                    rawTitle ||
                    (isUpdateTool ? (lookup?.title ?? "Document") : "");
                  const artifactType =
                    rawType ||
                    (isUpdateTool
                      ? (lookup?.type ?? "text/markdown")
                      : "");
                  accumulatedText += `\n\n[artifact:${artifactId}:${artifactTitle}:${artifactType}]`;
                  if (artifactId) {
                    artifactStore.setActiveArtifact(artifactId, {
                      openPanel: false,
                    });
                  }
                  yield createYieldObject();
                }
                continue;

              case "finish":
                streamFinished = true;
                continue;
            }
          }

          // AI SDK protocol `finish` event ends the stream cleanly
          if (streamFinished) {
            if (accumulatedText || accumulatedReasoning || sources.length > 0) {
              yield createYieldObject();
            }
            break;
          }

          // Handle current API format
          const delta = chunk.delta;
          if (delta !== undefined && delta !== null) {
            if (
              accumulatedReasoning &&
              !hasAddedReasoningToTextDelay &&
              !accumulatedText
            ) {
              hasAddedReasoningToTextDelay = true;
              reasoningPhaseStore.setDone();
              await new Promise((resolve) => setTimeout(resolve, 400));
            }
            accumulatedText += delta;
          }

          if (chunk.content) {
            accumulatedText += chunk.content;
          }

          if (chunk.message) {
            if (chunk.message.id && !serverMessageId) {
              serverMessageId = chunk.message.id;
            }
            accumulatedText += chunk.message.content || "";
          }

          // Tool input events: notify host about non-artifact tool calls.
          // Backend may send these as dedicated events (toolName + args).
          if (
            onToolCall &&
            chunk.toolName &&
            chunk.toolName !== "create_artifact" &&
            chunk.toolName !== "update_artifact"
          ) {
            onToolCall(
              chunk.toolName,
              (chunk.args ?? {}) as Record<string, unknown>,
              buildToolCallCtx(),
            );
            const inlineToolCallId =
              typeof chunk.toolCallId === "string" && chunk.toolCallId.length > 0
                ? chunk.toolCallId
                : undefined;
            if (inlineToolCallId && chatId) {
              const payload: ChatRequest = {
                chatId,
                messages: [
                  {
                    role: "tool",
                    content: HOST_TOOL_ACK_JSON,
                    toolCallId: inlineToolCallId,
                  },
                ],
                stream: false,
              };
              const mid = resolveModelId();
              if (mid && mid.trim() !== "") {
                payload.modelId = mid;
              }
              try {
                await postToolAckAndMerge(payload);
              } catch (submitErr) {
                if (submitErr instanceof CuadraApiError) {
                  pushAttachmentError(submitErr.message, "API_ERROR");
                } else {
                  pushAttachmentError(
                    submitErr instanceof Error
                      ? submitErr.message
                      : "Tool result failed",
                    "API_ERROR",
                  );
                }
              }
            }
            // Preserve any assistant text already streamed; empty final yield can clear the bubble.
            yield createYieldObject();
            streamFinished = true;
            completedAfterHostToolCall = true;
            break streamLoop;
          }

          // Message-stream format tool calls:
          // - If artifacts are enabled, we parse create_artifact/update_artifact and inject markers.
          // - If the host provided onToolCall, we also parse non-artifact tool calls (e.g. go_to).
          if (
            (enableArtifacts || onToolCall) &&
            Array.isArray(chunk.toolCalls) &&
            chunk.toolCalls.length > 0
          ) {
            const messageKey = chunk.id ?? toolCallMessageKey;
            for (let i = 0; i < chunk.toolCalls.length; i++) {
              const tc = chunk.toolCalls[i] as {
                id?: string | null;
                function?: { name?: string; arguments?: string };
              };
              const key = `${messageKey}:${i}`;
              if (typeof tc.id === "string" && tc.id.length > 0) {
                toolCallIdAccumulator.set(key, tc.id);
              }
              const nameDelta = tc?.function?.name;
              if (typeof nameDelta === "string" && nameDelta) {
                toolCallNameAccumulator.set(key, nameDelta);
              }
              const name = toolCallNameAccumulator.get(key);
              const isArtifactTool =
                name === "create_artifact" || name === "update_artifact";
              const hasExistingAccumulation = toolCallArgsAccumulator.has(key);
              // For non-artifact tools, only accumulate/handle if the host provided a handler.
              if (!isArtifactTool && !hasExistingAccumulation && !onToolCall)
                continue;
              if (artifactMarkerEmittedKeys.has(key)) continue;
              if (enableArtifacts && isArtifactTool && !hasExistingAccumulation) {
                artifactStore.beginDocumentGeneration();
                if (!artifactGeneratingFired && chatId) {
                  artifactGeneratingFired = true;
                  onArtifactGenerating?.(chatId);
                }
              }
              if (enableArtifacts) {
                streamingMetadataStore.setPendingArtifact(true);
              }
              const argsDelta = tc.function?.arguments ?? "";
              const prev = toolCallArgsAccumulator.get(key) ?? "";
              const combined = prev + argsDelta;
              toolCallArgsAccumulator.set(key, combined);
              try {
                const args = JSON.parse(combined) as Record<string, unknown>;
                const identifier =
                  typeof args.identifier === "string" ? args.identifier : "";
                const title = typeof args.title === "string" ? args.title : "";
                const type = typeof args.type === "string" ? args.type : "";
                const isUpdateArtifact = name === "update_artifact";
                const existing =
                  identifier.length > 0
                    ? artifactStore.getArtifact(identifier)
                    : undefined;
                const markerTitle =
                  title ||
                  (isUpdateArtifact ? (existing?.title ?? "Document") : "");
                const markerType =
                  type ||
                  (isUpdateArtifact
                    ? (existing?.type ?? "text/markdown")
                    : "");
                const canEmitArtifactMarker =
                  enableArtifacts &&
                  identifier &&
                  (isUpdateArtifact
                    ? !!(markerTitle && markerType)
                    : !!(title && type));
                if (canEmitArtifactMarker) {
                  accumulatedText += `\n\n[artifact:${identifier}:${markerTitle}:${markerType}]`;
                  artifactMarkerEmittedKeys.add(key);
                  artifactStore.setActiveArtifact(identifier, {
                    openPanel: false,
                  });
                  streamingMetadataStore.setPendingArtifact(false);
                  yield createYieldObject();
                }

                // Non-artifact tool calls: once args parse, notify host and end the stream.
                if (!isArtifactTool && onToolCall && typeof name === "string" && name) {
                  onToolCall(name, args, buildToolCallCtx());
                  hostToolCallEmittedKeys.add(key);
                  try {
                    await submitHostToolAck(key);
                  } catch (submitErr) {
                    if (submitErr instanceof CuadraApiError) {
                      pushAttachmentError(submitErr.message, "API_ERROR");
                    } else {
                      pushAttachmentError(
                        submitErr instanceof Error
                          ? submitErr.message
                          : "Tool result failed",
                        "API_ERROR",
                      );
                    }
                  }
                  yield createYieldObject();
                  streamFinished = true;
                  completedAfterHostToolCall = true;
                  break streamLoop;
                }
              } catch {
                // Incomplete JSON, keep accumulating
              }
            }
          }

          // Flush: args may be complete in the accumulator even if this chunk has
          // no `toolCalls` (e.g. early `finished` was processed before the last delta).
          if (onToolCall) {
            for (const [flushKey, combined] of toolCallArgsAccumulator) {
              if (hostToolCallEmittedKeys.has(flushKey)) continue;
              const flushName = toolCallNameAccumulator.get(flushKey);
              if (!flushName || typeof flushName !== "string") continue;
              const flushIsArtifact =
                flushName === "create_artifact" ||
                flushName === "update_artifact";
              if (flushIsArtifact) continue;
              try {
                const flushArgs = JSON.parse(combined) as Record<
                  string,
                  unknown
                >;
                onToolCall(flushName, flushArgs, buildToolCallCtx());
                hostToolCallEmittedKeys.add(flushKey);
                try {
                  await submitHostToolAck(flushKey);
                } catch (submitErr) {
                  if (submitErr instanceof CuadraApiError) {
                    pushAttachmentError(submitErr.message, "API_ERROR");
                  } else {
                    pushAttachmentError(
                      submitErr instanceof Error
                        ? submitErr.message
                        : "Tool result failed",
                      "API_ERROR",
                    );
                  }
                }
                yield createYieldObject();
                streamFinished = true;
                completedAfterHostToolCall = true;
                break streamLoop;
              } catch {
                // still incomplete JSON
              }
            }
          }

          if (chunk.done) {
            streamFinished = true;
            break;
          }

          if (chunk.finished) {
            // API may send `finished: true` before the last `toolCalls` argument
            // delta arrives, or toggle `finished` again. Do not end the run while
            // streamed tool JSON is still incomplete (option 2: raw Cuadra SSE).
            if (!chunk.error && hasIncompleteStreamedToolWork()) {
              continue streamLoop;
            }
            streamFinished = true;
            if (chunk.error) {
              streamErrorReceived = true;
              const isCreditsError =
                chunk.error.code === "insufficient_credits" ||
                chunk.error.code === "quota_exceeded";
              const isOverage = chunk.error.code === "credit_overage";
              const isPremiumUpgrade =
                chunk.error.code === "premium_model_requires_upgrade";
              const isDailyLimit =
                chunk.error.code === "daily_limit_exceeded";
              const isServiceError = chunk.error.code === "service_error";
              const code = isDailyLimit
                ? "DAILY_LIMIT_EXHAUSTED"
                : isOverage
                  ? "CREDIT_OVERAGE"
                  : isCreditsError
                    ? "INSUFFICIENT_CREDITS"
                    : isPremiumUpgrade
                      ? "PREMIUM_MODEL_UPGRADE"
                      : isServiceError
                        ? "SERVICE_ERROR"
                        : "API_ERROR";
              pushAttachmentError(chunk.error.message, code);
            }
            if (accumulatedText || accumulatedReasoning || sources.length > 0) {
              yield createYieldObject();
            }
            break;
          }

          if (accumulatedText || accumulatedReasoning || sources.length > 0) {
            yield createYieldObject();
          }
        }

        // Final yield with accumulated content
        const hasContent = !!(
          accumulatedText ||
          accumulatedReasoning ||
          sources.length > 0
        );
        if (hasContent) {
          yield createYieldObject();
        }

        const wasCancelled = !!abortSignal?.aborted;

        if (wasCancelled) {
          // User explicitly stopped generation — not an error.
        } else if (
          !streamErrorReceived &&
          (hasContent || completedAfterHostToolCall)
        ) {
          chatErrorStore.clear();
          chatErrorStore.resetEmptyFailures();
        } else if (
          !streamErrorReceived &&
          !hasContent &&
          !completedAfterHostToolCall
        ) {
          const emptyStreamOwnerId =
            chatId ??
            serverChatId ??
            (threadId
              ? localToRemoteIdMap.get(threadId) || threadId
              : undefined) ??
            activeThreadRemoteId ??
            "";
          if (emptyStreamOwnerId) {
            chatErrorStore.setEmptyStreamError(emptyStreamOwnerId);
          }
        }
      } finally {
        // Ensure reasoning phase is closed even if no delta arrived
        reasoningPhaseStore.setDone();
        streamingMetadataStore.setPendingArtifact(false);

        let artifactIncomplete = false;
        if (enableArtifacts) {
          const snap = artifactStore.getSnapshot();
          const activeArtifact = artifactStore.getActiveArtifact();
          artifactIncomplete =
            isDocumentArtifactGenerating(snap.artifactUiPhase) ||
            // activeArtifactId is set (e.g. via setActiveArtifact with an
            // identifier) but the actual artifact hasn't been created yet
            // (no data-artifact-start received).
            (!!snap.activeArtifactId && !activeArtifact) ||
            (activeArtifact?.status === "streaming" && !activeArtifact.content);
        }

        if (chatId) {
          activeStreamingChatIds.delete(chatId);

          const wasInterrupted =
            !ephemeral &&
            !abortSignal?.aborted &&
            ((!streamFinished && !streamErrorReceived) || artifactIncomplete);

          if (enableArtifacts) {
            if (
              abortSignal?.aborted ||
              (artifactIncomplete && !wasInterrupted)
            ) {
              artifactStore.closePanel();
            }
          }

          if (wasInterrupted) {
            backgroundGenerationStore.startPolling(
              chatId,
              (id) => client.getChat(id),
              onStreamEnd,
            );
          } else {
            onStreamEnd?.(chatId);
          }
        } else {
          if (enableArtifacts) {
            if (abortSignal?.aborted || artifactIncomplete) {
              artifactStore.closePanel();
            }
          }
        }
      }
    },
  };
}

